/**
 * Frontend tier feature helpers — pure client-side.
 *
 * Mirrors `tier_features.py` semantics:
 *   - Tier hierarchy: none < investor_lite < investor_pro < investor_plus
 *   - hasFeature(user, featureId) — quick check
 *   - tierLabel(tier) — display label
 *   - tierAtLeast(tier, minTier) — ordering helper
 *
 * The authoritative feature map comes from the backend via `/api/auth/me` ->
 * `user.tier.features` (a boolean map). This file only exposes ordering +
 * label helpers so the SPA never has to duplicate the matrix.
 */

const TIER_ORDER = {
    none: 0,
    investor_lite: 1,
    investor_pro: 2,
    investor_plus: 3,
};

export const TIER_LABEL = {
    investor_lite: "Investor Lite",
    investor_pro: "Investor Pro",
    investor_plus: "Investor Plus",
};

export const TIER_PRICE_USD = {
    investor_lite: 29,
    investor_pro: 79,
    investor_plus: 149,
};

export function normalizeTier(tier) {
    if (!tier || !(tier in TIER_ORDER)) return "none";
    return tier;
}

export function tierAtLeast(tier, minTier) {
    return (
        TIER_ORDER[normalizeTier(tier)] >= TIER_ORDER[normalizeTier(minTier)]
    );
}

export function tierLabel(tier) {
    return TIER_LABEL[normalizeTier(tier)] || null;
}

/**
 * hasFeature(user, featureId) — when `user` is the `/auth/me` payload,
 * looks up `user.tier.features[featureId]`. Returns false when unknown.
 */
export function hasFeature(user, featureId) {
    if (!user || !user.tier || !user.tier.features) return false;
    return !!user.tier.features[featureId];
}

/**
 * minTierFor(user, featureId) — pulls the minimum tier required from the
 * `/auth/me` payload (`user.tier.feature_min_tier`). Used by upgrade CTAs.
 */
export function minTierFor(user, featureId) {
    if (!user || !user.tier || !user.tier.feature_min_tier) return null;
    return user.tier.feature_min_tier[featureId] || null;
}

/**
 * upgradeTargetFor(user, featureId) — returns the cheapest tier the user
 * needs to upgrade to in order to unlock the feature. Returns null if
 * already unlocked.
 */
export function upgradeTargetFor(user, featureId) {
    if (hasFeature(user, featureId)) return null;
    return minTierFor(user, featureId);
}
